/**
 * PosPinManager
 * ─────────────────────────────────────────────────────────────────────────────
 * Lists the cashiers attached to a vendor (owner + seller helpers) and lets an
 * authorised user set/reset each cashier's 4-digit POS terminal PIN.
 *
 * Two modes:
 *   - Vendor self-service (default): omit `vendorUserId`; uses
 *     GET /api/pos/cashiers + PUT /api/pos/cashier-pin.
 *   - Admin override: pass `admin` + `vendorUserId`; uses
 *     GET /api/pos/cashiers?vendorUserId=… + PUT /api/pos/admin/cashier-pin.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  apiPosListCashiers,
  apiPosSetCashierPin,
  apiPosAdminSetCashierPin,
} from "../../api/index";
import { AppButton } from "../ui";

const S = {
  wrap: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 6px 20px rgba(15,23,42,0.05)", overflow: "hidden" },
  header: { padding: "18px 24px", borderBottom: "1px solid #eef2f6" },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" },
  sub: { margin: "3px 0 0", fontSize: 12, color: "#64748b" },
  body: { padding: "8px 0" },
  row: { display: "flex", alignItems: "center", gap: 16, padding: "14px 24px", borderBottom: "1px solid #f8fafc" },
  name: { fontWeight: 600, color: "#111827", fontSize: "0.92rem" },
  meta: { fontSize: "0.78rem", color: "#6b7280" },
  badge: (set) => ({
    fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 12,
    background: set ? "#dcfce7" : "#fef3c7", color: set ? "#166534" : "#92400e",
  }),
  roleChip: { fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "#eef2ff", color: "#4338ca", marginLeft: 8 },
  // The PIN box is the kit's input (className lk-input) at a fixed width.
  input: { width: 110, letterSpacing: 4, textAlign: "center" },
  spacer: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 },
};

const PosPinManager = ({ vendorUserId = null, admin = false, title = "Terminal PINs" }) => {
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pins, setPins] = useState({}); // cashierId -> input value
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiPosListCashiers(vendorUserId);
      setCashiers(data.cashiers || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load cashiers.");
    } finally {
      setLoading(false);
    }
  }, [vendorUserId]);

  useEffect(() => {
    // Admin mode needs a selected vendor before it can load.
    if (admin && !vendorUserId) {
      setCashiers([]);
      setLoading(false);
      return;
    }
    load();
  }, [load, admin, vendorUserId]);

  const handleSave = async (cashierId) => {
    const pin = (pins[cashierId] || "").trim();
    if (!/^\d{4}$/.test(pin)) {
      toast.error("PIN must be exactly 4 digits.");
      return;
    }
    setSavingId(cashierId);
    try {
      if (admin) {
        await apiPosAdminSetCashierPin(vendorUserId, cashierId, pin);
      } else {
        await apiPosSetCashierPin(cashierId, pin);
      }
      toast.success("PIN updated.");
      setPins((p) => ({ ...p, [cashierId]: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update PIN.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ ...S.wrap, padding: 40, textAlign: "center" }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  if (admin && !vendorUserId) {
    return (
      <div style={{ ...S.wrap, padding: 28, textAlign: "center", color: "#6b7280" }}>
        Select a vendor to manage their terminal PINs.
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h4 style={S.title}>{title}</h4>
        <p style={S.sub}>
          Set the 4-digit PIN each person uses to log in to the Lusuku POS terminal.
        </p>
      </div>
      <div style={S.body}>
        {error && (
          <div style={{ padding: "14px 24px", color: "#b91c1c", fontSize: "0.85rem" }}>{error}</div>
        )}
        {!error && cashiers.length === 0 && (
          <div style={{ padding: "20px 24px", color: "#6b7280", fontSize: "0.85rem" }}>
            No cashiers found.
          </div>
        )}
        {cashiers.map((c) => (
          <div key={c.cashierId} style={S.row}>
            <div>
              <div style={S.name}>
                {c.name || c.email}
                <span style={S.roleChip}>{c.role}</span>
              </div>
              <div style={S.meta}>{c.email}</div>
            </div>
            <div style={S.spacer}>
              <span style={S.badge(c.hasCustomPin)}>
                {c.hasCustomPin ? "PIN set" : "Default 0000"}
              </span>
              <input
                className="lk-input"
                style={S.input}
                aria-label={`New 4-digit PIN for ${c.name || c.email}`}
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder="••••"
                value={pins[c.cashierId] || ""}
                onChange={(e) =>
                  setPins((p) => ({
                    ...p,
                    [c.cashierId]: e.target.value.replace(/\D/g, "").slice(0, 4),
                  }))
                }
              />
              <AppButton
                loading={savingId === c.cashierId}
                disabled={(pins[c.cashierId] || "").length !== 4}
                onClick={() => handleSave(c.cashierId)}
                style={{ minWidth: 96, justifyContent: "center" }}
              >
                {c.hasCustomPin ? "Reset PIN" : "Set PIN"}
              </AppButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PosPinManager;
